import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import OrganizerLogin from './components/Organizer/OrganizerLogin';
import OrganizerDashboard from './components/Organizer/OrganizerDashboard';
import SpectatorView from './components/Spectator/SpectatorView';
import RegistrationPage from './components/Registration/RegistrationPage';
import LoadingSpinner from './components/Common/LoadingSpinner';
import { authService, matchesService } from './services/api';

function SpectatorRoute() {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    loadCurrentMatches();
    const interval = setInterval(loadCurrentMatches, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadCurrentMatches = async () => {
    try {
      const response = await matchesService.getCurrent();
      if (response.success) setMatches(response.data);
    } catch (error) {
      console.error('Erro ao carregar partidas:', error);
    }
  };

  return (
    <SpectatorView
      matches={matches}
      onSwitchToOrganizer={() => navigate('/organizer')}
      onRefreshMatches={loadCurrentMatches}
    />
  );
}

function OrganizerRoute() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(authService.isAuthenticated());
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleTokenExpired = () => {
      setIsLoggedIn(false);
    };

    window.addEventListener('authTokenExpired', handleTokenExpired);
    return () => window.removeEventListener('authTokenExpired', handleTokenExpired);
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      loadCurrentMatches();
    }
  }, [isLoggedIn]);

  const loadCurrentMatches = async () => {
    try {
      const response = await matchesService.getCurrent();
      if (response.success) setMatches(response.data);
    } catch (error) {
      console.error('Erro ao carregar partidas:', error);
    }
  };

  const handleLogin = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.login(credentials);
      if (response.success) {
        setIsLoggedIn(true);
      } else {
        setError('Credenciais inválidas');
      }
    } catch (error) {
      setError(error.error?.message || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } finally {
      setIsLoggedIn(false);
      navigate('/');
    }
  };

  const updateMatchScore = async (matchId, playerId, action) => {
    try {
      const response = await matchesService.updateScore(matchId, { playerId, action });
      if (response.success) {
        setMatches(prev => prev.map(match => {
          if (match.id === matchId) {
            return {
              ...match,
              player1: { ...match.player1, goals: response.data.player1.goals },
              player2: { ...match.player2, goals: response.data.player2.goals },
              goals: response.data.goals,
            };
          }
          return match;
        }));
      }
    } catch (error) {
      alert('Erro ao atualizar placar: ' + (error.error?.message || 'Erro desconhecido'));
      loadCurrentMatches();
    }
  };

  const finishMatch = async (matchId) => {
    try {
      const response = await matchesService.finishMatch(matchId);
      if (response.success) {
        alert('Partida finalizada com sucesso!');
        await loadCurrentMatches();
      }
    } catch (error) {
      alert('Erro ao finalizar partida: ' + (error.error?.message || 'Erro desconhecido'));
    }
  };

  if (loading) return <LoadingSpinner />;

  if (!isLoggedIn) {
    return (
      <OrganizerLogin
        onLogin={handleLogin}
        onSwitchToSpectator={() => navigate('/')}
        error={error}
        loading={loading}
      />
    );
  }

  return (
    <OrganizerDashboard
      matches={matches}
      onLogout={handleLogout}
      onUpdateScore={updateMatchScore}
      onFinishMatch={finishMatch}
      onMatchesUpdate={loadCurrentMatches}
    />
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<SpectatorRoute />} />
        <Route path="/inscricao" element={<RegistrationPage />} />
        <Route path="/organizer" element={<OrganizerRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
